<template>
    <div>
        <b-form @submit.prevent="createLowInventoryPO">
            <div class="row mb-3">
                <div class="col-md-6">
                    <b-form-group label="PO #" label-for="po-number" :state="!!lowInventoryPOForm.poNumber">
                        <b-form-input
                            id="po-number"
                            v-model="lowInventoryPOForm.poNumber"
                            required
                            placeholder="Enter PO number"
                            :state="!!lowInventoryPOForm.poNumber"
                        ></b-form-input>
                        <b-form-invalid-feedback>PO Number is required</b-form-invalid-feedback>
                    </b-form-group>
                </div>
                <div class="col-md-6">
                    <b-form-group label="BILL TO" label-for="billing-location" :state="!!lowInventoryPOForm.billing_location">
                        <b-form-select
                            id="billing-location"
                            v-model="lowInventoryPOForm.billing_location"
                            :options="billing_location_options"
                            required
                            @change="handleBillingLocationChange"
                            :state="!!lowInventoryPOForm.billing_location"
                        ></b-form-select>
                        <b-form-invalid-feedback>Billing Location is required</b-form-invalid-feedback>
                    </b-form-group>
                </div>
            </div>
            
            <div class="row mb-3">
                <div class="col-md-6">
                    <b-form-group label="SHIP TO" label-for="shipping-location" :state="!!lowInventoryPOForm.shipping_location">
                        <b-form-select
                            id="shipping-location"
                            v-model="lowInventoryPOForm.shipping_location"
                            :options="shipping_location_options"
                            required
                            @change="onShippingLocationChange"
                            :state="!!lowInventoryPOForm.shipping_location"
                        ></b-form-select>
                        <b-form-invalid-feedback>Shipping Location is required</b-form-invalid-feedback>
                    </b-form-group>
                </div>
                <div class="col-md-6">
                    <b-form-group label="RECEIVING LOCATION" label-for="receiving-location" :state="!!lowInventoryPOForm.receivingLocation">
                        <b-form-select
                            id="receiving-location"
                            v-model="lowInventoryPOForm.receivingLocation"
                            :options="filteredReceivingLocations"
                            required
                            :state="!!lowInventoryPOForm.receivingLocation"
                        ></b-form-select>
                        <b-form-invalid-feedback>Receiving Location is required</b-form-invalid-feedback>
                    </b-form-group>
                </div>
            </div>
            
            <div class="row mb-3">
                <div class="col-md-6">
                    <b-form-group label="REQUEST ETA" label-for="eta">
                        <date-picker
                            id="eta"
                            v-model="lowInventoryPOForm.eta"
                            format="MM-DD-YYYY"
                            valueType="format"
                            style="width: 100%;"
                        ></date-picker>
                    </b-form-group>
                </div>
                <div class="col-md-6">
                    <b-form-group label="STATUS" label-for="status">
                        <b-form-select
                            id="status"
                            v-model="lowInventoryPOForm.status"
                            :options="vendor_order_statuses"
                            required
                        ></b-form-select>
                    </b-form-group>
                </div>
            </div>

            <!-- Default Quantity for all items -->
            <div class="row mb-3">
                <div class="col-md-12">
                    <b-form-group label="DEFAULT ORDER QUANTITY PER ITEM" label-for="default-quantity">
                        <b-form-input
                            id="default-quantity"
                            v-model="lowInventoryPOForm.defaultQuantity"
                            type="number"
                            min="1"
                            placeholder="Enter default quantity for all items"
                            :state="lowInventoryPOForm.defaultQuantity > 0"
                        ></b-form-input>
                        <b-form-text>This quantity will be applied to all selected items</b-form-text>
                        <b-form-invalid-feedback>Default quantity must be at least 1</b-form-invalid-feedback>
                    </b-form-group>
                </div>
            </div>

            <hr>

            <!-- Add PDF Generation Options -->
            <div class="row mb-3">
                <div class="col-md-12 d-flex justify-content-between align-items-center">
                    <!-- Left side: checkbox -->
                    <b-form-group class="mb-0">
                        <b-form-checkbox v-model="lowInventoryPOForm.sendEmail">
                            Send PDF via Email
                        </b-form-checkbox>
                    </b-form-group>

                    <!-- Right side: button -->
                    <b-button
                        v-if="allFieldsFilled"
                        variant="info"
                        @click.stop.prevent="generateLowInventoryPDF"
                        class="ml-2"
                    >
                        Generate and View PDF
                    </b-button>
                </div>
            </div>

            <!-- Email input shown only when checkbox checked -->
            <div class="row mb-3" v-if="lowInventoryPOForm.sendEmail">
                <div class="col-md-12">
                    <b-form-group label="Email Address" label-for="email">
                        <b-form-input
                            id="email"
                            v-model="lowInventoryPOForm.email"
                            type="email"
                            placeholder="Enter email address"
                            :state="lowInventoryPOForm.sendEmail ? !!lowInventoryPOForm.email : null"
                            class="custom-email-width"
                        ></b-form-input>
                        <b-form-invalid-feedback>
                            Valid email is required when sending via email
                        </b-form-invalid-feedback>
                    </b-form-group>
                </div>
            </div>

            <hr>

            <div class="row mb-3">
                <div class="col-md-12">
                    <h6>Selected Low Inventory Items ({{ selectedItems.length }})</h6>
                    <div class="selected-items-container" style="max-height: 300px; overflow-y: auto;">
                        <table class="table table-sm table-bordered">
                            <thead>
                                <tr style="background-color: #f8f9fa;">
                                    <th style="width: 120px;">SKU</th>
                                    <th>PRODUCT NAME</th>
                                    <th style="width: 100px;">CURRENT</th>
                                    <th style="width: 100px;">RESERVED</th>
                                    <th style="width: 100px;">AVAILABLE</th>
                                    <th style="width: 100px;">ORDER QTY</th>
                                    <th style="width: 100px;">UNIT COST</th>
                                    <th style="width: 100px;">TOTAL</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="(item, index) in selectedItems" :key="index" 
                                    :style="{ backgroundColor: getItemRowColor(item) }">
                                    <td style="font-weight: bold;">{{ item.product_sku }}</td>
                                    <td>{{ item.product_name }}</td>
                                    <td :class="{'text-danger': item.in_stock <= 2}">{{ item.in_stock }}</td>
                                    <td>{{ item.reserved }}</td>
                                    <td :class="getAvailableClass(item)">{{ item.available }}</td>
                                    <td>
                                        <b-form-input
                                            size="sm"
                                            type="number"
                                            v-model="item.orderQuantity"
                                            :min="1"
                                            :state="item.orderQuantity > 0"
                                            style="width: 90px;"
                                            @input="updateItemOrderQuantity(item, $event)"
                                        ></b-form-input>
                                    </td>
                                    <td>${{ parseFloat(item.landed_cost).toFixed(2) }}</td>
                                    <td style="font-weight: bold;">${{ calculateItemTotal(item).toFixed(2) }}</td>
                                </tr>
                                <!-- Total Row -->
                                <tr style="background-color: #e8f4fc; font-weight: bold;">
                                    <td colspan="5" style="text-align: right;">TOTALS:</td>
                                    <td>{{ totalOrderQuantity }}</td>
                                    <td></td>
                                    <td>${{ totalOrderCost.toFixed(2) }}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                    
                    <!-- Summary Stats -->
                    <div class="row mt-3">
                        <div class="col-md-4">
                            <div class="stat-box" style="background-color: #ffebee; padding: 10px; border-radius: 5px;">
                                <strong>Critical Items (≤ 2):</strong> {{ criticalCount }}
                            </div>
                        </div>
                        <div class="col-md-4">
                            <div class="stat-box" style="background-color: #fff3e0; padding: 10px; border-radius: 5px;">
                                <strong>Out of Stock:</strong> {{ outOfStockCount }}
                            </div>
                        </div>
                        <div class="col-md-4">
                            <div class="stat-box" style="background-color: #e8f4fc; padding: 10px; border-radius: 5px;">
                                <strong>Low Items (3-4):</strong> {{ lowCount }}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <hr>

            <div class="d-flex justify-content-end mt-4">
                <b-button variant="secondary" @click="cancel" class="mr-2">
                    Cancel
                </b-button>

                <b-button
                    variant="primary"
                    type="submit"
                    :disabled="!allFieldsFilled || isCreatingPO"
                >
                    <b-spinner small v-if="isCreatingPO" class="mr-2"></b-spinner>
                    {{ isCreatingPO ? 'Creating PO...' : 'Create Low Inventory PO' }}
                </b-button>
            </div>
        </b-form>
    </div>
</template>

<script>
import moment from 'moment'
import Requestor from '@/utilities/Requestor';
import DatePicker from 'vue2-datepicker';
import 'vue2-datepicker/index.css';

export default {
    name: "CreateLowInventoryPO",
    props: {
        selectedItems: {
            type: Array,
            default: () => []
        }
    },
    components: { DatePicker },
    data: function(){
        return {
            lowInventoryPOForm: {
                poNumber: '',
                receivingLocation: null,
                eta: null,
                status: 1,
                sendEmail: false,
                email: '',
                shipping_location: null,
                billing_location: null,
                defaultQuantity: 5
            },
            isCreatingPO: false,
            vendor_order_statuses: [
                {value: 1, text: "NEW"},
                {value: 2, text: "ORDERED"},
                {value: 3, text: "SCHEDULED"},
                {value: 4, text: "IN-TRANSIT"},
                {value: 5, text: "PARTIAL"},
                {value: 6, text: "RECEIVED"},
                {value: 7, text: "COMPLETED"},
                {value: 8, text: "ARCHIVED"},
            ],
            packet: {},
            initialized: false
        }
    },
    
    computed: {
        billing_location_options() {
            if (!this.packet.locations) return [];
            
            const options = [];
            
            // Add Mecca Wholesale as the first option
            const meccaWholesale = { value: 47, text: "MECCA WHOLESALE" };
            options.push(meccaWholesale);
            
            // Add all store locations from shipping_location_options, excluding specific locations
            if (this.shipping_location_options && this.shipping_location_options.length > 0) {
                const storeLocations = this.shipping_location_options.filter(loc =>
                    loc.value !== 47 &&
                    loc.text !== 'MECCA WHOLESALE' &&
                    loc.value !== null &&
                    loc.text &&
                    !loc.text.includes('FOLCROFT') &&
                    !loc.text.includes('9TH ST') &&
                    !loc.text.includes('9th ST')
                );
                
                options.push(...storeLocations);
            }
            
            return options;
        },
        
        shipping_location_options() {
            if (!this.packet.locations) return [];
            const excludedLocationTexts = [' ALL', 'OUT OF STOCK', 'ONLINE SALES', 'MECCA SHIPPING'];
            
            let options = this.packet.locations ? [...this.packet.locations] : [];
            
            // Filter out excluded texts and empty/null texts
            options = options.filter(loc => 
                loc.text && 
                !excludedLocationTexts.some(excluded => 
                    loc.text.toUpperCase().includes(excluded.toUpperCase())
                )
            );
            
            // Check if we have any warehouse locations
            const warehouseLocations = ['9TH ST ', '101 WH', 'FOLCROFT'];
            const hasWarehouseLocation = options.some(loc => 
                warehouseLocations.some(wh => loc.text.toUpperCase().includes(wh.toUpperCase()))
            );
            
            // Check if we already have MECCA WHOLESALE
            const hasMeccaWholesale = options.some(loc => 
                loc.text.toUpperCase() === 'MECCA WHOLESALE'
            );
            
            // If we have warehouse locations but no MECCA WHOLESALE, add it
            if (hasWarehouseLocation && !hasMeccaWholesale) {
                options.unshift({
                    value: 47,
                    text: 'MECCA WHOLESALE'
                });
            }
            
            return options;
        },
        
        receiving_location_options() {
            if (!this.packet.locations) return [];
            
            // Filter out unwanted locations
            return this.packet.locations.filter(loc => {
                if (!loc.text) return false;
                
                const textUpper = loc.text.toUpperCase();
                const excluded = [
                    'ALL',
                    'OUT OF STOCK', 
                    'ONLINE SALES',
                    'MECCA SHIPPING'
                ];
                
                return !excluded.some(ex => textUpper.includes(ex));
            });
        },
        
        filteredReceivingLocations() {
            const shipToLocation = this.lowInventoryPOForm.shipping_location;
            
            if (!shipToLocation || !this.receiving_location_options.length) {
                return this.receiving_location_options;
            }
            
            const shipToText = this.getLocationText(shipToLocation);
            
            if (shipToText === 'MECCA WHOLESALE') {
                // Show only the three specific warehouse locations for Mecca Wholesale
                return this.receiving_location_options.filter(loc => {
                    if (!loc.text) return false;
                    const textUpper = loc.text.toUpperCase();
                    return textUpper.includes('9TH ST') || 
                           textUpper.includes('101 WH') || 
                           textUpper.includes('FOLCROFT');
                });
            } else {
                // For store locations, show only that specific location
                return this.receiving_location_options.filter(loc =>
                    loc.value === shipToLocation || loc.text === shipToText
                );
            }
        },
        
        selectedVendor() {
            if (this.selectedItems.length === 0) return null;
            const vendorIds = [...new Set(this.selectedItems.map(item => item.vendor_id))];
            if (vendorIds.length > 1) return { value: null, text: "Multiple Vendors" };
            return {
                value: this.selectedItems[0].vendor_id,
                text: this.selectedItems[0].vendor
            };
        },
        
        totalOrderQuantity() {
            return this.selectedItems.reduce((total, item) => {
                const qty = this.getItemOrderQuantity(item);
                return total + parseInt(qty);
            }, 0);
        },
        
        totalOrderCost() {
            return this.selectedItems.reduce((total, item) => {
                const qty = this.getItemOrderQuantity(item);
                return total + (item.landed_cost * qty);
            }, 0);
        },
        
        criticalCount() {
            return this.selectedItems.filter(item => item.available <= 2).length;
        },
        
        outOfStockCount() {
            return this.selectedItems.filter(item => item.available <= 0).length;
        },
        
        lowCount() {
            return this.selectedItems.filter(item => item.available >= 3 && item.available <= 4).length;
        },
        
        allFieldsFilled() {
            return this.lowInventoryPOForm.poNumber &&
                   this.lowInventoryPOForm.receivingLocation &&
                   this.lowInventoryPOForm.shipping_location &&
                   this.lowInventoryPOForm.billing_location &&
                   this.lowInventoryPOForm.defaultQuantity > 0;
        }
    },
    
    watch: {
        'lowInventoryPOForm.defaultQuantity': function(newVal) {
            if (newVal > 0 && this.selectedItems) {
                // Update order quantities for items that don't have custom quantities
                this.selectedItems.forEach(item => {
                    if (!item.hasCustomQuantity) {
                        item.orderQuantity = newVal;
                    }
                });
            }
        },
        
        selectedItems: {
            handler(newItems) {
                if (newItems && newItems.length > 0 && !this.initialized) {
                    this.initializeForm();
                }
            },
            immediate: true,
            deep: true
        }
    },
    
    methods: {
        getAvailableClass(item) {
            if (item.available <= 0) return 'text-danger font-weight-bold';
            if (item.available <= 2) return 'text-warning font-weight-bold';
            return 'text-success';
        },
        
        getItemRowColor(item) {
            if (item.available <= 0) return '#ffebee';
            if (item.available <= 2) return '#fff3e0';
            return '#ffffff';
        },
        
        getLocationText(locationValue) {
            if (!locationValue) return '';
            
            if (locationValue === 47) {
                return 'MECCA WHOLESALE';
            }
            
            const location = this.packet.locations.find(loc => loc.value === locationValue);
            return location ? location.text : '';
        },
        
        handleBillingLocationChange() {
            // Bill to is independent - no automatic changes
        },
        
        onShippingLocationChange(newLocation) {
            if (newLocation) {
                this.handleShippingLocationChange();
            }
        },
        
        handleShippingLocationChange() {
            const shipToLocation = this.lowInventoryPOForm.shipping_location;
            
            if (shipToLocation && shipToLocation !== 47) {
                // For store locations, automatically set receiving location to match
                this.lowInventoryPOForm.receivingLocation = shipToLocation;
            } else {
                this.lowInventoryPOForm.receivingLocation = null;
            }
        },
        
        calculateItemTotal(item) {
            const qty = this.getItemOrderQuantity(item);
            return item.landed_cost * qty;
        },
        
        getItemOrderQuantity(item) {
            return item.orderQuantity || this.lowInventoryPOForm.defaultQuantity || 1;
        },
        
        updateItemOrderQuantity(item, value) {
            // Mark that this item has a custom quantity
            item.hasCustomQuantity = true;
            item.orderQuantity = parseInt(value) || 0;
        },
        
        generateLowInventoryPDF() {
            if (!this.allFieldsFilled) {
                this.$bvToast.toast("Please fill in all required fields", {
                    title: "Error",
                    autoHideDelay: 1500,
                    variant: "danger"
                });
                return;
            }
            
            const pdfData = {
                po_number: this.lowInventoryPOForm.poNumber,
                vendor: this.selectedVendor ? this.selectedVendor.text : 'Unknown Vendor',
                receiving_location: this.getLocationText(this.lowInventoryPOForm.receivingLocation),
                shipping_location: this.getLocationText(this.lowInventoryPOForm.shipping_location),
                billing_location: this.getLocationText(this.lowInventoryPOForm.billing_location),
                eta: this.lowInventoryPOForm.eta,
                status: this.getStatusText(this.lowInventoryPOForm.status),
                items: this.selectedItems.map(item => ({
                    sku: item.product_sku,
                    name: item.product_name,
                    current_stock: item.in_stock,
                    reserved: item.reserved,
                    available: item.available,
                    order_quantity: this.getItemOrderQuantity(item),
                    unit_cost: item.landed_cost,
                    total_cost: this.calculateItemTotal(item)
                })),
                total_quantity: this.totalOrderQuantity,
                total_cost: this.totalOrderCost,
                send_email: false
            };
            
            Requestor.postSyncP(
                "/api/sales/generate_low_inventory_pdf/",
                pdfData,
                this,
                (response) => {
                    if (response.data && response.data.error) {
                        this.$bvToast.toast(response.data.error, {
                            title: "Error",
                            autoHideDelay: 1500,
                            variant: "danger"
                        });
                        return;
                    }
                    
                    try {
                        const blob = new Blob([response.data], { type: "application/pdf" });
                        const pdfUrl = URL.createObjectURL(blob);
                        window.open(pdfUrl, "_blank");
                    } catch (err) {
                        console.error("Error processing PDF:", err);
                        this.$bvToast.toast("Failed to display PDF", {
                            title: "Error",
                            autoHideDelay: 1500,
                            variant: "danger"
                        });
                    }
                },
                (error) => {
                    console.error("PDF generation error:", error);
                    this.$bvToast.toast("Failed to generate PDF", {
                        title: "Error",
                        autoHideDelay: 1500,
                        variant: "danger"
                    });
                }
            );
        },
        
        createLowInventoryPO() {
            if (this.isCreatingPO) {
                this.$bvToast.toast("Purchase order creation already in progress", {
                    title: "Please wait",
                    variant: "warning",
                    autoHideDelay: 1500
                });
                return;
            }
            
            // Validate all fields
            if (!this.allFieldsFilled) {
                this.$bvToast.toast("Please fill in all required fields", {
                    title: "Validation Error",
                    variant: "danger",
                    autoHideDelay: 1500
                });
                return;
            }
            
            // Validate email if sending is selected
            if (this.lowInventoryPOForm.sendEmail) {
                if (!this.lowInventoryPOForm.email) {
                    this.$bvToast.toast("Please enter a valid email address", {
                        title: "Error",
                        autoHideDelay: 1500,
                        variant: "danger"
                    });
                    return;
                }
                
                const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                if (!emailRegex.test(this.lowInventoryPOForm.email)) {
                    this.$bvToast.toast("Please enter a valid email address format", {
                        title: "Error",
                        autoHideDelay: 1500,
                        variant: "danger"
                    });
                    return;
                }
            }
            
            this.isCreatingPO = true;
            
            const payload = {
                po_number: this.lowInventoryPOForm.poNumber,
                vendor_id: this.selectedVendor ? this.selectedVendor.value : null,
                vendor_name: this.selectedVendor ? this.selectedVendor.text : 'Unknown Vendor',
                receiving_location_id: this.lowInventoryPOForm.receivingLocation,
                shipping_location: this.lowInventoryPOForm.shipping_location,
                shipping_location_name: this.getLocationText(this.lowInventoryPOForm.shipping_location),
                billing_location_name: this.getLocationText(this.lowInventoryPOForm.billing_location),
                billing_location: this.lowInventoryPOForm.billing_location,
                status: this.lowInventoryPOForm.status,
                receiving_location_name: this.getLocationText(this.lowInventoryPOForm.receivingLocation),
                eta: this.lowInventoryPOForm.eta
                    ? moment(this.lowInventoryPOForm.eta, 'MM-DD-YYYY').format('YYYY-MM-DD')
                    : null,
                created_by: this.packet.userDetails ? this.packet.userDetails.username : 'Unknown',
                send_email: this.lowInventoryPOForm.sendEmail,
                email: this.lowInventoryPOForm.sendEmail ? this.lowInventoryPOForm.email : '',
                items: this.selectedItems.map(item => ({
                    product_sku: item.product_sku,
                    product_name: item.product_name,
                    quantity: this.getItemOrderQuantity(item),
                    landed_cost: item.landed_cost,
                    location_id: item.location_id,
                    locsub: item.locsub,
                    in_stock: item.in_stock,
                    available: item.available,
                    reserved: item.reserved,
                    category: item.category,
                    vendor_id: item.vendor_id,
                    vendor_sku: item.vendor_sku || '',
                    properties: item.properties || {}
                }))
            };
            
            const that = this;
            const path = "/api/sales/create_low_inventory_po/";
            
            Requestor.postAsyncP(
                path,
                payload,
                this,
                function(response) {
                    that.isCreatingPO = false;
                    
                    if (response.data.error) {
                        that.$bvToast.toast(response.data.error, {
                            title: "Error",
                            autoHideDelay: 1500,
                            variant: "danger"
                        });
                    } else {
                        let successMessage = "Low Inventory PO created successfully!";
                        
                        if (that.lowInventoryPOForm.sendEmail) {
                            successMessage += ` Email sent to ${that.lowInventoryPOForm.email}`;
                        }
                        
                        that.$bvToast.toast(successMessage, {
                            title: "Success",
                            autoHideDelay: 2000,
                            variant: "success"
                        });
                        
                        that.resetForm();
                        that.$emit('po-created');
                    }
                },
                function(error) {
                    that.isCreatingPO = false;
                    console.error("API Error:", error);
                    that.$bvToast.toast("Failed to create PO", {
                        title: "Error",
                        autoHideDelay: 1500,
                        variant: "danger"
                    });
                }
            );
        },
        
        resetForm() {
            this.lowInventoryPOForm = {
                poNumber: '',
                receivingLocation: null,
                shipping_location: null,
                billing_location: null,
                eta: null,
                status: 1,
                sendEmail: false,
                email: '',
                defaultQuantity: 5
            };
            this.isCreatingPO = false;
            this.initialized = false;
        },
        
        cancel() {
            this.resetForm();
            this.$emit('cancel');
        },
        
        getStatusText(statusCode) {
            const statusMap = {
                1: "NEW",
                2: "ORDERED",
                3: "SCHEDULED",
                4: "IN-TRANSIT",
                5: "PARTIAL",
                6: "RECEIVED",
                7: "COMPLETED",
                8: "ARCHIVED"
            };
            return statusMap[statusCode] || statusCode;
        },
        
        initializeForm() {
            if (this.initialized || !this.selectedItems || this.selectedItems.length === 0) {
                return;
            }
            
            // Auto-generate PO number
            const vendorPrefix = this.selectedVendor ? 
                this.selectedVendor.text.substring(0, 3).toUpperCase() : 'LI';
            const dateStr = new Date().toISOString().slice(2, 10).replace(/-/g, '');
            const randomNum = Math.floor(Math.random() * 900) + 100;
            this.lowInventoryPOForm.poNumber = `${vendorPrefix}-${dateStr}-${randomNum}`;
            
            // Set default billing location to "MECCA WHOLESALE"
            const meccaWholesale = this.billing_location_options.find(
                loc => loc.text === "MECCA WHOLESALE"
            );
            
            if (meccaWholesale) {
                this.lowInventoryPOForm.billing_location = meccaWholesale.value;
            }
            
            // Set default order quantities for items
            this.selectedItems.forEach(item => {
                // Set order quantity to default or (available + 2), whichever is larger
                const defaultQty = Math.max(this.lowInventoryPOForm.defaultQuantity || 5, item.available + 2);
                item.orderQuantity = defaultQty;
                item.hasCustomQuantity = false;
            });
            
            this.initialized = true;
        }
    },
    
    created: function() {
        try {
            this.packet = JSON.parse(localStorage.packet);
            this.packet.userDetails = JSON.parse(localStorage.userDetails);
        } catch (error) {
            console.error("Error loading packet from localStorage:", error);
            this.packet = { locations: [], userDetails: { username: 'Unknown' } };
        }
    },
    
    mounted() {
        // Initialize form when component is mounted
        this.$nextTick(() => {
            if (this.selectedItems && this.selectedItems.length > 0) {
                this.initializeForm();
            }
        });
    }
}
</script>

<style scoped>
.custom-email-width {
    width: 270px !important;
    max-width: 100%;
}

.selected-items-container {
    border: 1px solid #dee2e6;
    border-radius: 5px;
    padding: 10px;
}

.stat-box {
    text-align: center;
    border: 1px solid #dee2e6;
    margin-bottom: 10px;
}
</style>


// WEBPACK FOOTER //
// src/components/VendorOrders/CreateLowInventoryPO.vue